/**
 * Generated building interiors: a floor plan per (building, floor) on the world tile grid.
 * Deterministic from the building key + seed, computed lazily and cached, identical on
 * client and server (nothing goes over the wire). See docs/DESIGN_INTERIORS.md.
 */
import type { PlacedBuilding } from './buildings';
import { hashStr, Rng } from './rng';

export const CELL_WALL = 1, CELL_FLOOR = 2, CELL_DOOR = 3, CELL_WINDOW = 4, CELL_STAIRS = 5, CELL_ELEV = 6, CELL_FURN = 7;
export type InteriorKit = 'house' | 'apartment' | 'office' | 'shop' | 'hall' | 'open' | 'garage';

/** The floor a building's garage is: below the ground floor, entered by its own door on the street side (owner, Q76). */
export const GARAGE_FLOOR = -1;
/** One parking bay in metres: a car is 1.7 × 4.25, a pickup 2 × 5.5; the bay is the tile rectangle a stored car is drawn on. */
export const BAY_W = 3, BAY_D = 5;
/** the volume budget one bay buys — a sedan is ~11 m³, so a bay is two small cars' worth or one van's (owner: "m³ limit") */
export const BAY_M3 = 20;

/** A parking bay: tiles, and which way a car in it faces (0 S, 1 W, 2 N, 3 E — into the aisle, nose out). */
export interface Bay { x: number; y: number; w: number; h: number; facing: number }
/**
 * What a garage takes (owner, Q79: *"m³ limit + a length and width limit so you can't drive a bus inside"*). A vehicle
 * has to pass all four, and the refusal names the one it failed (§24 of MAPGEN_DECISION.md): `width + 0.3 ≤ doorWidth`,
 * `height ≤ doorHeight`, `length + 0.4 ≤ innerLength`, and stored volume + its own ≤ `m3`.
 */
export interface GarageSpec { doorWidth: number; doorHeight: number; innerLength: number; m3: number; bays: Bay[] }

export interface Unit { i: number; x0: number; y0: number; x1: number; y1: number; door: [number, number] }
/** the smallest block that gets flats (the short side): a corridor along one wall and rooms of two tiles behind it; from `FLATS_DOUBLE` the corridor runs down the middle with rooms of three tiles either side */
export const FLATS_MIN = 6, FLATS_DOUBLE = 11;
/** a flat's property key: the block's key, the floor and the unit — `x,y,<f>u<i>` (the first two numbers still parse as the block's origin everywhere a key is split) */
export const unitKey = (b: { x: number; y: number }, floor: number, i: number): string => `${b.x},${b.y},${floor}u${i}`;
export function unitOfKey(key: string): { f: number; i: number } | undefined { const m = /,(\d+)u(\d+)$/.exec(key); return m ? { f: Number(m[1]), i: Number(m[2]) } : undefined; }
export const unitLetter = (i: number): string => String.fromCharCode(65 + (i % 26));
/** the unit a world point is in, on this plan */
export function unitAt(plan: FloorPlan, wx: number, wy: number): Unit | undefined { const x = Math.floor(wx) - plan.x, y = Math.floor(wy) - plan.y; return plan.units?.find((u) => x >= u.x0 && x <= u.x1 && y >= u.y0 && y <= u.y1); }
export interface FloorPlan {
  /** world-tile origin and size (same as the building footprint) */
  x: number; y: number; w: number; h: number;
  /** (owner, Sep 29) an upper floor's fire door onto the building's fire escape (world tile; buildings.ts `fe`) — locked from outside */
  fireDoor?: [number, number];
  floor: number;
  kit: InteriorKit;
  /** cell types, row-major */
  cells: Uint8Array;
  /** entrance door tiles (ground floor) */
  doors: [number, number][];
  /** stair core tiles (all floors), empty when single storey */
  stairs: [number, number][];
  elevator?: [number, number];
  /** height of a storey in metres (floor k sits at k * storyH) */
  storyH: number;
  /** (owner, Sep 22 — docs/PLAN_HOME.md "Flats") a big apartment block: the units off the corridor, local cells (inclusive rects) and each one's door; absent when the plan has none */
  units?: Unit[];
  /** furniture (content props with placement "indoor") on CELL_FURN cells, world tiles */
  furniture: { x: number; y: number; id: string; height: number; /** (Sep 22) quarter turns of a placed piece (game/stuff.ts) */ r?: number }[];
  /** the garage floor only: what it takes and where the cars stand (world tiles) */
  garage?: GarageSpec;
}

/** Indoor furniture catalogue (props with placement "indoor"); set once from the registry by WorldStream. */
export interface FurnitureDef { id: string; height: number; rooms: InteriorKit[]; indoor: 'wall' | 'back' | 'front' | 'anywhere'; density: number; /** (owner, Sep 22) sold in the stores only (tag `store`): never generated into a plan, so the catalogue can grow without a house re-furnishing under its owner */ store?: boolean }
let furnitureCatalog: FurnitureDef[] = [];
export function setFurniture(defs: FurnitureDef[]): void { furnitureCatalog = defs.slice().sort((a, b) => a.id.localeCompare(b.id)); cache.clear(); }

export function kitFor(b: PlacedBuilding): InteriorKit | undefined {
  const d = b.def;
  if (b.ruined) return undefined;
  if (d.interior === 'none' || b.height <= 0 || b.w < 3 || b.h < 3) return undefined;
  if (typeof d.interior === 'object' && d.interior?.kit) return d.interior.kit;
  const t = new Set(d.tags);
  const has = (...k: string[]) => k.some((x) => t.has(x));
  if (has('parking', 'helipad', 'garage', 'lot')) return 'open';
  if (has('shop', 'retail', 'store', 'cafe', 'restaurant', 'bar', 'market', 'kiosk')) return 'shop';
  if (has('warehouse', 'industrial', 'factory', 'hangar', 'depot', 'workshop', 'stadium', 'church', 'station')) return 'hall';
  if (has('apartment', 'apartments', 'tower', 'condo', 'flats') || (d.stories >= 4 && has('residential', 'house'))) return 'apartment';
  if (has('office', 'civic', 'hospital', 'school', 'hotel', 'bank', 'police', 'library')) return 'office';
  if (has('house', 'residential', 'villa', 'cottage', 'cabin', 'farm')) return 'house';
  return d.stories >= 4 ? 'office' : 'house';
}

const cache = new Map<string, FloorPlan | null>();
/** (owner, Sep 22) a place's own furniture (game/stuff.ts): by property key ("x,y"); when set, every floor's plan draws this list instead of generating */
export interface PlacedStuff { id: string; x: number; y: number; f: number; r?: number }
const stuffOverrides = new Map<string, PlacedStuff[]>();
const plansOf = new Map<string, Set<string>>(); // property key → the cache keys of its plans (invalidated when its stuff changes)
export function setStuff(key: string, list: PlacedStuff[] | undefined): void { if (list) stuffOverrides.set(key, list); else stuffOverrides.delete(key); for (const k of plansOf.get(key) ?? []) cache.delete(k); plansOf.delete(key); }
export function stuffOf(key: string): PlacedStuff[] | undefined { return stuffOverrides.get(key); }
export function furnitureHeight(id: string): number { return furnitureCatalog.find((d) => d.id === id)?.height ?? 0.6; }
function applyStuff(cells: Uint8Array, w: number, h: number, bx: number, by: number, floor: number, list: PlacedStuff[]): { x: number; y: number; id: string; height: number; r?: number }[] {
  for (let i = 0; i < cells.length; i++) if (cells[i] === CELL_FURN) cells[i] = CELL_FLOOR;
  const out: { x: number; y: number; id: string; height: number; r?: number }[] = [];
  for (const s of list) { if (s.f !== floor) continue; const lx = s.x - bx, ly = s.y - by; if (lx < 0 || ly < 0 || lx >= w || ly >= h || cells[ly * w + lx] !== CELL_FLOOR) continue; cells[ly * w + lx] = CELL_FURN; out.push({ x: lx, y: ly, id: s.id, height: furnitureHeight(s.id), ...(s.r ? { r: s.r } : {}) }); } // (local tiles, as furnish() returns them — build() adds the origin)
  return out;
}

/** Forget a building's cached plans (after it collapsed). */
export function clearPlans(b: PlacedBuilding): void { for (const k of [...cache.keys()]) if (k.startsWith(`${b.key}:`)) cache.delete(k); }

/** The plan of one floor (0 = ground), or undefined if the building has no interior. */
export function floorPlan(b: PlacedBuilding, floor: number, seed: number): FloorPlan | undefined {
  const key = `${b.key}:${floor}:${seed}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit ?? undefined;
  if (cache.size > 4000) cache.clear();
  const plan = build(b, floor, seed);
  cache.set(key, plan ?? null);
  { const pk = b.main ?? `${b.x},${b.y}`; let s = plansOf.get(pk); if (!s) { s = new Set(); plansOf.set(pk, s); } s.add(key); }
  return plan;
}

/** Where the front door sits along the street wall — the first thing the ground floor's rng decides, so the garage can ask too. */
function entranceCol(kit: InteriorKit, along: number, rng: Rng): number {
  const centre = Math.floor(along / 2);
  return kit === 'shop' || kit === 'hall' || along < 6 ? centre : Math.max(1, Math.min(along - 2, centre + rng.int(-Math.floor(along / 4), Math.floor(along / 4))));
}

function build(b: PlacedBuilding, floor: number, seed: number): FloorPlan | undefined {
  const kit = kitFor(b);
  if (!kit) return undefined;
  if (floor === GARAGE_FLOOR) return buildGarage(b, kit, seed);
  if (floor < 0 || floor >= b.def.stories) return undefined;
  const { w, h } = b;
  const rng = new Rng(hashStr(`${b.key}:${floor}`, seed));
  const cells = new Uint8Array(w * h).fill(CELL_FLOOR);
  const at = (x: number, y: number) => y * w + x;
  const set = (x: number, y: number, c: number) => { if (x >= 0 && y >= 0 && x < w && y < h) cells[at(x, y)] = c; };
  // shell
  for (let x = 0; x < w; x++) { set(x, 0, CELL_WALL); set(x, h - 1, CELL_WALL); }
  for (let y = 0; y < h; y++) { set(0, y, CELL_WALL); set(w - 1, y, CELL_WALL); }
  // street side: rot 0 → road south, 1 → west, 2 → north, 3 → east
  const side = b.rot; // 0 S, 1 W, 2 N, 3 E
  const doors: [number, number][] = [];
  const doorAt = (t: number): [number, number] => side === 0 ? [t, h - 1] : side === 2 ? [t, 0] : side === 1 ? [0, t] : [w - 1, t];
  const along = side % 2 === 0 ? w : h;
  if (floor === 0) {
    const centre = Math.floor(along / 2);
    const d1 = entranceCol(kit, along, rng);
    doors.push(doorAt(d1));
    if (along >= 14 && kit !== 'house') doors.push(doorAt(Math.max(1, Math.min(along - 2, d1 + (d1 < centre ? 1 : -1) * Math.floor(along / 2)))));
    if (kit === 'hall' && along >= 8) { doors.push(doorAt(Math.max(1, d1 - 1))); doors.push(doorAt(Math.min(along - 2, d1 + 1))); } // wide doors on halls
    for (const [dx, dy] of doors) set(dx, dy, CELL_DOOR);
  }
  // (owner, Sep 22) the doors between a house's blocks (buildings.ts joinBlocks): a door cell on this block's wall, every floor — the sibling's is the tile next to it
  const joins = (b.joins ?? []).map(([jx, jy]) => [jx - b.x, jy - b.y] as [number, number]).filter(([jx, jy]) => jx >= 0 && jy >= 0 && jx < w && jy < h);
  for (const [jx, jy] of joins) set(jx, jy, CELL_DOOR);
  // (owner, Sep 29) an upper floor opens onto the fire escape: a door cell in the wall the iron stairs stand against
  const fire: [number, number] | undefined = floor >= 1 && b.fe ? [b.fe.door[0] - b.x, b.fe.door[1] - b.y] : undefined;
  if (fire) set(fire[0], fire[1], CELL_DOOR);
  // windows every 2–3 tiles on exterior walls (not corners, not doors); shops: big ground-floor front
  const wps = b.def.wall.windowsPerStory ?? 1;
  const step = wps >= 3 ? 2 : wps >= 1 ? 3 : 0;
  if (step) {
    const isDoor = (x: number, y: number) => cells[at(x, y)] === CELL_DOOR;
    const wrng = new Rng(hashStr(`${b.key}:windows`, seed)); // same columns on every floor (the facade is drawn from them)
    for (let x = 1 + (wrng.int(0, step - 1)); x < w - 1; x += step) { if (!isDoor(x, 0)) set(x, 0, CELL_WINDOW); if (!isDoor(x, h - 1)) set(x, h - 1, CELL_WINDOW); }
    for (let y = 1 + (wrng.int(0, step - 1)); y < h - 1; y += step) { if (!isDoor(0, y)) set(0, y, CELL_WINDOW); if (!isDoor(w - 1, y)) set(w - 1, y, CELL_WINDOW); }
    if (kit === 'shop' && floor === 0) for (let t = 1; t < along - 1; t++) { const [x, y] = doorAt(t); if (cells[at(x, y)] === CELL_WALL) set(x, y, CELL_WINDOW); }
  }
  // stair core (2×2) and elevator, same cells on every floor, against the wall opposite the entrance
  const stairs: [number, number][] = [];
  let elevator: [number, number] | undefined;
  const core = coreAt(b, seed);
  if (core) {
    for (const [x, y] of core.stairs) { stairs.push([x, y]); set(x, y, CELL_STAIRS); }
    if (core.elevator) { elevator = core.elevator; set(elevator[0], elevator[1], CELL_ELEV); }
  }
  // (owner, Sep 22) a big apartment block: a corridor down the middle with the flats off it, each behind its own door (docs/PLAN_HOME.md "Flats")
  let units: Unit[] | undefined;
  if (kit === 'apartment' && Math.min(w, h) >= FLATS_MIN) { const trial = cells.slice(); const u = layoutFlats(trial, w, h, side, doors, core, rng); if (u.length) { cells.set(trial); units = u; } } // (a block too cramped for a single flat keeps its rooms)
  // rooms: BSP inside the shell; walls with a door; kits decide the minimum room size
  if (kit !== 'open' && kit !== 'hall' && !units) {
    const minRoom = kit === 'house' ? 3 : kit === 'shop' ? 6 : kit === 'apartment' ? 3 : 4;
    const protectedCell = (x: number, y: number) => cells[at(x, y)] === CELL_STAIRS || cells[at(x, y)] === CELL_ELEV;
    const walls: { vertical: boolean; pos: number; from: number; to: number }[] = [];
    const split = (x0: number, y0: number, x1: number, y1: number, depth: number): void => {
      const rw = x1 - x0 + 1, rh = y1 - y0 + 1;
      const need = minRoom * 2 - 1; // two rooms of at least minRoom-1 plus the wall
      if (depth > 5 || (rw < need && rh < need)) return;
      const vertical = rw >= rh ? rw >= need : rh < need;
      if (vertical) {
        if (rw < need) return;
        const sx = rng.int(x0 + minRoom - 1, x1 - minRoom + 1);
        for (let y = y0; y <= y1; y++) if (protectedCell(sx, y)) return; // no wall through the stair core
        for (let y = y0; y <= y1; y++) set(sx, y, CELL_WALL);
        walls.push({ vertical: true, pos: sx, from: y0, to: y1 });
        split(x0, y0, sx - 1, y1, depth + 1); split(sx + 1, y0, x1, y1, depth + 1);
      } else {
        if (rh < need) return;
        const sy = rng.int(y0 + minRoom - 1, y1 - minRoom + 1);
        for (let x = x0; x <= x1; x++) if (protectedCell(x, sy)) return;
        for (let x = x0; x <= x1; x++) set(x, sy, CELL_WALL);
        walls.push({ vertical: false, pos: sy, from: x0, to: x1 });
        split(x0, y0, x1, sy - 1, depth + 1); split(x0, sy + 1, x1, y1, depth + 1);
      }
    };
    split(1, 1, w - 2, h - 2, 0);
    // doors: after all splits, so every door has floor on both sides; long walls get two
    const open = (x: number, y: number) => x > 0 && y > 0 && x < w - 1 && y < h - 1 && cells[at(x, y)] !== CELL_WALL && cells[at(x, y)] !== CELL_WINDOW;
    for (const wl of walls) {
      const cand: [number, number][] = [];
      for (let t = wl.from; t <= wl.to; t++) {
        const [x, y] = wl.vertical ? [wl.pos, t] : [t, wl.pos];
        if (cells[at(x, y)] !== CELL_WALL) continue;
        if (wl.vertical ? open(x - 1, y) && open(x + 1, y) : open(x, y - 1) && open(x, y + 1)) cand.push([x, y]);
      }
      if (!cand.length) continue;
      const n = wl.to - wl.from > 10 ? 2 : 1;
      for (let k = 0; k < n; k++) { const [x, y] = cand[Math.floor(rng.float() * cand.length)]; set(x, y, CELL_DOOR); }
    }
    // entrance doors must open onto floor: clear the cell behind them
    for (const [dx, dy] of [...doors, ...joins, ...(fire ? [fire] : [])]) { const ix = dx === 0 ? 1 : dx === w - 1 ? w - 2 : dx, iy = dy === 0 ? 1 : dy === h - 1 ? h - 2 : dy; if (cells[at(ix, iy)] === CELL_WALL) set(ix, iy, CELL_FLOOR); }
  }
  if (fire) { const [dx, dy] = fire, ix = dx === 0 ? 1 : dx === w - 1 ? w - 2 : dx, iy = dy === 0 ? 1 : dy === h - 1 ? h - 2 : dy; if (cells[at(ix, iy)] === CELL_WALL || cells[at(ix, iy)] === CELL_WINDOW) set(ix, iy, CELL_FLOOR); } // (the fire door opens onto floor, flats and halls too)
  const generated = furnish(cells, w, h, kit, side, rng, fire ? [...doors, fire] : doors);
  const own = floor >= 0 ? stuffOverrides.get(b.main ?? `${b.x},${b.y}`) : undefined; // (a wing's furniture is its house's list — the pieces in this block's tiles)
  const furniture = own ? applyStuff(cells, w, h, b.x, b.y, floor, own) : generated; // (a place with its own stuff: the list, not the generator's)
  return { ...(units ? { units } : {}), ...(fire ? { fireDoor: [b.x + fire[0], b.y + fire[1]] as [number, number] } : {}), x: b.x, y: b.y, w, h, floor, kit, cells, doors: doors.map(([x, y]) => [b.x + x, b.y + y]), stairs: stairs.map(([x, y]) => [b.x + x, b.y + y]), elevator: elevator ? [b.x + elevator[0], b.y + elevator[1]] : undefined, storyH: b.def.storyHeight ?? 3, furniture: furniture.map((f) => ({ ...f, x: b.x + f.x, y: b.y + f.y })) };
}

/**
 * The flats: a one-tile corridor along the block's long axis through its middle, a strip of rooms on either side cut into units
 * about six tiles wide, a wall between each unit and the corridor with the unit's door in the middle, walls between the units.
 * The entrance door reaches the corridor by a stub, the stair core sits where it sits (`coreAt`): any unit those cut through is
 * dropped and its walls left standing — a lobby off the corridor. Local cells; deterministic from the plan's rng.
 */
function layoutFlats(cells: Uint8Array, w: number, h: number, side: number, doors: [number, number][], core: { stairs: [number, number][]; elevator?: [number, number] } | undefined, rng: Rng): Unit[] {
  const at = (x: number, y: number) => y * w + x;
  const long = w >= h, L = long ? w : h, S = long ? h : w; // (a, b): a along the corridor, b across it
  const P = (a: number, b: number): [number, number] => (long ? [a, b] : [b, a]);
  const keep = (x: number, y: number) => cells[at(x, y)] === CELL_STAIRS || cells[at(x, y)] === CELL_ELEV || cells[at(x, y)] === CELL_DOOR;
  const put = (a: number, b: number, c: number) => { const [x, y] = P(a, b); if (x > 0 && y > 0 && x < w - 1 && y < h - 1 && !keep(x, y)) cells[at(x, y)] = c; };
  // the corridor: down the middle with flats both sides when the block is wide enough (rooms of three tiles behind a wall each side), else
  // along the long wall opposite the entrance — where the stair core stands (`coreAt`), so the stairs open off the corridor and the flats
  // keep the other side; a door in an end wall reaches the corridor along that end (a hall a tile wide, no flat there)
  const doorLow = doors.some(([dx, dy]) => (long ? dy === 0 : dx === 0)), doorHigh = doors.some(([dx, dy]) => (long ? dy === h - 1 : dx === w - 1));
  const double = S >= FLATS_DOUBLE, corr = double ? Math.floor(S / 2) : doorLow && !doorHigh ? S - 2 : 1;
  for (let a = 1; a < L - 1; a++) put(a, corr, CELL_FLOOR);
  const strips: { b0: number; b1: number; wall: number }[] = double ? [{ b0: 1, b1: corr - 2, wall: corr - 1 }, { b0: corr + 2, b1: S - 2, wall: corr + 1 }] : corr === 1 ? [{ b0: 3, b1: S - 2, wall: 2 }] : [{ b0: 1, b1: S - 4, wall: S - 3 }];
  // the end walls: a hall beside a door there, and where the stair core stands against an end (a door in an end wall puts it at the other)
  const coreA = (core?.stairs ?? []).map(([x, y]) => (long ? x : y)), coreLo = coreA.length && Math.min(...coreA) <= 2, coreHi = coreA.length && Math.max(...coreA) >= L - 3;
  const lo = doors.some(([dx, dy]) => (long ? dx : dy) === 0) || coreLo ? (coreLo ? Math.max(...coreA) + 1 : 1) : 0, hi = doors.some(([dx, dy]) => (long ? dx : dy) === L - 1) || coreHi ? (coreHi ? Math.min(...coreA) - 1 : L - 2) : L - 1;
  const hallOk = (a: number) => a <= lo || a >= hi; // (the halls at the ends: open floor from the corridor to the core and the door)
  const n = Math.max(1, Math.floor((hi - lo) / 4)), cut: number[] = []; for (let k = 1; k < n; k++) cut.push(lo + Math.round(((hi - lo) * k) / n)); // (flats three or four tiles wide)
  const units: Unit[] = []; let i = 0;
  for (const st of strips) {
    for (let a = 1; a < L - 1; a++) if (!hallOk(a)) put(a, st.wall, CELL_WALL);
    for (const a of [lo, ...cut, hi]) if (a > 0 && a < L - 1) for (let b = Math.min(st.b0, st.wall); b <= Math.max(st.b1, st.wall); b++) put(a, b, CELL_WALL); // (between the flats, and between a flat and a hall)
    const edges = [lo, ...cut, hi];
    for (let k = 0; k + 1 < edges.length; k++) {
      const a0 = edges[k] + 1, a1 = edges[k + 1] - 1; if (a1 - a0 + 1 < 3) continue;
      const da = Math.floor((a0 + a1) / 2); put(da, st.wall, CELL_DOOR);
      const [x0, y0] = P(a0, st.b0), [x1, y1] = P(a1, st.b1), [dx, dy] = P(da, st.wall);
      units.push({ i: i++, x0: Math.min(x0, x1), y0: Math.min(y0, y1), x1: Math.max(x0, x1), y1: Math.max(y0, y1), door: [dx, dy] });
    }
  }
  // the entrance reaches the corridor by a stub straight in from the door; the stub and the stair core turn the units they run through into the lobby
  const stubs: [number, number][] = [];
  for (const [dx, dy] of doors) {
    const [da, db] = long ? [dx, dy] : [dy, dx]; // (the door in the corridor's frame)
    if (db === 0 || db === S - 1) { for (let b = db === 0 ? 1 : S - 2; b !== corr; b += db === 0 ? 1 : -1) { stubs.push(P(da, b)); put(da, b, CELL_FLOOR); } } // (a long wall: straight across to the corridor)
    else { const a = da === 0 ? 1 : L - 2; for (let b = db; b !== corr; b += db < corr ? 1 : -1) { stubs.push(P(a, b)); put(a, b, CELL_FLOOR); } } // (a short wall: along the end wall to the corridor)
  }
  const taken = [...stubs, ...(core?.stairs ?? []), ...(core?.elevator ? [core.elevator] : [])];
  const lobby = units.filter((u) => taken.some(([x, y]) => x >= u.x0 && x <= u.x1 && y >= u.y0 && y <= u.y1));
  for (const u of lobby) for (let y = u.y0; y <= u.y1; y++) for (let x = u.x0; x <= u.x1; x++) if (!keep(x, y)) cells[at(x, y)] = CELL_FLOOR;
  const out = units.filter((u) => !lobby.includes(u)); out.forEach((u, k) => { u.i = k; });
  void rng; void side;
  return out;
}
/** (owner, Sep 22) how many rooms a floor has: the floor's regions the walls and doors cut it into, two tiles or more each (the property site's facts) */
export function roomCount(plan: FloorPlan): number {
  const { w, h, cells } = plan, seen = new Uint8Array(w * h); let rooms = 0;
  const open = (i: number) => cells[i] !== CELL_WALL && cells[i] !== CELL_WINDOW && cells[i] !== CELL_DOOR;
  for (let i0 = 0; i0 < w * h; i0++) {
    if (seen[i0] || !open(i0)) continue;
    let n = 0; const stack = [i0]; seen[i0] = 1;
    while (stack.length) { const i = stack.pop()!; n++; const x = i % w, y = (i - x) / w; for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = x + dx, ny = y + dy; if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue; const k = ny * w + nx; if (!seen[k] && open(k)) { seen[k] = 1; stack.push(k); } } }
    if (n >= 2) rooms++;
  }
  return rooms;
}
/** The stair core (2×2) and elevator, in local cells — the same on every floor, against the wall opposite the entrance. */
function coreAt(b: PlacedBuilding, seed: number): { stairs: [number, number][]; elevator?: [number, number] } | undefined {
  const { w, h } = b, side = b.rot;
  if (b.def.stories < 2 || w < 5 || h < 5) return undefined;
  const srng = new Rng(hashStr(`${b.key}:core`, seed)); // core position must not depend on the floor
  const cx = side === 1 ? w - 3 : side === 3 ? 1 : Math.max(1, Math.min(w - 3, Math.floor(w / 2) + srng.int(-2, 2)));
  const cy = side === 0 ? 1 : side === 2 ? h - 3 : Math.max(1, Math.min(h - 3, Math.floor(h / 2) + srng.int(-2, 2)));
  const stairs: [number, number][] = [[cx, cy], [cx + 1, cy], [cx, cy + 1], [cx + 1, cy + 1]];
  let elevator: [number, number] | undefined;
  if (b.def.stories >= 5) { const ex = cx + 2 < w - 1 ? cx + 2 : cx - 1; if (ex > 0 && ex < w - 1) elevator = [ex, cy]; }
  return { stairs, elevator };
}

/**
 * The garage floor (owner, Q76/Q79, and §26 of MAPGEN_DECISION.md): a floor of the building it belongs to, so nothing
 * new exists anywhere — `planAt` resolves it, the key-gated visibility works, walking works. It is a **walkable
 * interior** with parking bays; the cars in it are props, and getting into one is a transition that puts you outside
 * the door already driving. So the plan has no drivable tiles and no ramp geometry: a wide door on the street side
 * (beside the front door, never under it), an aisle, and bays nose-out against the back wall — and a second row along
 * the front wall when the building is deep enough. The stair core, if the building has one, comes down here too.
 *
 * Which buildings have one is not decided here: every building big enough gets a plan, and the game decides whether
 * the player may use it (a garage is bought, like a defence today). Too small for a bay and an aisle → no plan.
 */
function buildGarage(b: PlacedBuilding, kit: InteriorKit, seed: number): FloorPlan | undefined {
  const { w, h } = b, side = b.rot;
  if (kit === 'open') return undefined;
  const along = side % 2 === 0 ? w : h, deep = side % 2 === 0 ? h : w;
  if (along < 6 || deep < 2 + BAY_D) return undefined;
  // an aisle when the building is deep enough for one; a single garage has none — you drive straight into the bay
  const AISLE = deep - 2 >= BAY_D + 2 ? 2 : 0;
  // two rows when both fit with the aisle between; otherwise one row, as deep as 6 m if the building allows (a pickup)
  const twoRows = deep - 2 >= 2 * BAY_D + AISLE;
  const bayD = twoRows ? BAY_D : Math.min(BAY_D + 1, deep - 2 - AISLE);
  // (u, v): u along the street wall, v inward from it — the same plan whichever way the building faces
  const xy = (u: number, v: number): [number, number] => side === 0 ? [u, h - 1 - v] : side === 2 ? [u, v] : side === 1 ? [v, u] : [w - 1 - v, u];
  const cells = new Uint8Array(w * h).fill(CELL_FLOOR);
  const set = (x: number, y: number, c: number) => { if (x >= 0 && y >= 0 && x < w && y < h) cells[y * w + x] = c; };
  for (let x = 0; x < w; x++) { set(x, 0, CELL_WALL); set(x, h - 1, CELL_WALL); }
  for (let y = 0; y < h; y++) { set(0, y, CELL_WALL); set(w - 1, y, CELL_WALL); }
  // the door: 3 m on anything wide enough for it (a car is 1.7–2 m, a van 2.1), to one side of the front door
  const dw = along >= 9 ? 3 : 2;
  const uOf = (x: number, y: number) => side % 2 === 0 ? x - b.x : y - b.y;
  const front = (floorPlan(b, 0, seed)?.doors ?? []).map(([x, y]) => uOf(x, y));
  const e = front.length ? front.reduce((a, u) => a + u, 0) / front.length : along / 2;
  const clear = (u: number) => front.every((f) => f < u || f > u + dw - 1); // never on the front door's tiles
  const wish = Math.max(1, Math.min(along - 1 - dw, e <= along / 2 ? Math.ceil(e) + 2 : Math.floor(e) - 1 - dw));
  // the stair core comes down with the building
  const core = coreAt(b, seed);
  const stairs: [number, number][] = [];
  let elevator: [number, number] | undefined;
  if (core) {
    for (const [x, y] of core.stairs) { stairs.push([x, y]); set(x, y, CELL_STAIRS); }
    if (core.elevator) { elevator = core.elevator; set(elevator[0], elevator[1], CELL_ELEV); }
  }
  const blocked = (x: number, y: number) => cells[y * w + x] !== CELL_FLOOR;
  // bays: nose-out rows, the back wall first, the front wall too when there is room for both and the aisle between
  const bays: Bay[] = [];
  const taken = new Uint8Array(w * h);
  let u0 = -1;
  const tryBay = (u: number, v0: number, facing: number, dry = false): boolean => {
    if (u < 1 || u + BAY_W - 1 > along - 2) return false;
    // the tile rectangle this bay covers, in world-local cells
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let du = 0; du < BAY_W; du++) for (let dv = 0; dv < bayD; dv++) {
      const [x, y] = xy(u + du, v0 + dv);
      if (blocked(x, y) || taken[y * w + x]) return false;
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
    }
    // with an aisle, no bay stands in the doorway; without one, the bay in front of the door is the point
    if (AISLE && v0 === 1) for (let du = 0; du < BAY_W; du++) if (u + du >= u0 && u + du < u0 + dw) return false;
    if (dry) return true;
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) taken[y * w + x] = 1;
    bays.push({ x: b.x + x0, y: b.y + y0, w: x1 - x0 + 1, h: y1 - y0 + 1, facing });
    return true;
  };
  const backRow = deep - 1 - bayD;
  // the nearest u to the wish, either way, that `ok` accepts
  const nearest = (lo: number, hi: number, ok: (u: number) => boolean): number => {
    for (let d = 0; d < along; d++) { const l = wish - d, r = wish + d; if (l >= lo && l <= hi && ok(l)) return l; if (r >= lo && r <= hi && ok(r)) return r; }
    return -1;
  };
  let anchor = 1;
  if (AISLE) {
    u0 = nearest(1, along - 1 - dw, clear);
  } else {
    // a single garage: the door is in front of its bay, so the bay is placed first and the door fitted inside its columns
    const doorIn = (u: number) => { for (let d = u; d + dw - 1 <= u + BAY_W - 1; d++) if (clear(d)) return d; return -1; };
    anchor = nearest(1, along - 1 - BAY_W, (u) => tryBay(u, backRow, side, true) && doorIn(u) >= 0);
    if (anchor >= 0) u0 = doorIn(anchor);
  }
  if (u0 < 0) {
    // no room beside the front door: a hall's wide loading door is the garage door too (an on-ground garage); a
    // narrow front door with no wall to spare means no garage
    const lo = Math.min(...front), hi = Math.max(...front);
    if (front.length < dw || hi - lo + 1 !== front.length) return undefined;
    u0 = lo;
    if (!AISLE) { anchor = nearest(Math.max(1, lo + dw - BAY_W), Math.min(lo, along - 1 - BAY_W), (u) => tryBay(u, backRow, side, true)); if (anchor < 0) return undefined; }
  }
  const doors: [number, number][] = [];
  for (let u = u0; u < u0 + dw; u++) { const [x, y] = xy(u, 0); doors.push([x, y]); set(x, y, CELL_DOOR); }
  // a row: the anchor bay first, then packed outwards, sliding past the core a tile at a time
  const row = (v0: number, facing: number, first: number) => {
    let u = first;
    while (u >= 1 && !tryBay(u, v0, facing)) u--;
    for (let r = first + BAY_W; r + BAY_W - 1 <= along - 2; r++) if (tryBay(r, v0, facing)) r += BAY_W - 1;
    for (let l = first - BAY_W; l >= 1; l--) if (tryBay(l, v0, facing)) l -= BAY_W - 1;
  };
  row(backRow, side, AISLE ? 1 : anchor); // back wall, facing the street
  if (twoRows) row(1, (side + 2) % 4, 1); // front wall, facing in
  if (!bays.length) return undefined;
  const storyH = b.def.storyHeight ?? 3;
  const garage: GarageSpec = { doorWidth: dw, doorHeight: Math.round((storyH - 0.4) * 10) / 10, innerLength: bayD, m3: bays.length * BAY_M3, bays };
  return { x: b.x, y: b.y, w, h, floor: GARAGE_FLOOR, kit: 'garage', cells, doors: doors.map(([x, y]) => [b.x + x, b.y + y]), stairs: stairs.map(([x, y]) => [b.x + x, b.y + y]), elevator: elevator ? [b.x + elevator[0], b.y + elevator[1]] : undefined, storyH, furniture: [], garage };
}

/**
 * What a garage says to a vehicle: `undefined` when it fits, otherwise the sentence that names the rule it failed and
 * the numbers (owner: *"'garage full' message if the car doesn't fit"*; §24: four rules that all say no are one rule
 * that says nothing). `stored` is the volume already inside. Sizes in metres, the way vehicles carry them.
 */
export function garageRefuses(g: GarageSpec, v: { name: string; width: number; length: number; height: number }, stored: number): string | undefined {
  const m3 = v.width * v.length * v.height;
  if (v.width + 0.3 > g.doorWidth) return `The ${v.name} is ${v.width.toFixed(1)} m wide; the door is ${g.doorWidth.toFixed(1)} m`;
  if (v.height > g.doorHeight) return `The ${v.name} is ${v.height.toFixed(1)} m tall; the door is ${g.doorHeight.toFixed(1)} m`;
  if (v.length + 0.4 > g.innerLength) return `The ${v.name} is ${v.length.toFixed(1)} m long; this garage is ${g.innerLength.toFixed(1)} m inside`;
  if (stored + m3 > g.m3) return 'Garage full';
  return undefined;
}

/**
 * Furniture per room: flood-fill the floor into rooms (doors separate them), then put
 * `area / 7` pieces (max 5) on eligible cells — against a wall, at the back/front of the
 * room, or anywhere, as the prop asks — never next to a door or stairs, and never where it
 * would cut the room in two (checked by a BFS after each placement).
 */
function furnish(cells: Uint8Array, w: number, h: number, kit: InteriorKit, side: number, rng: Rng, doors: [number, number][]): { x: number; y: number; id: string; height: number }[] {
  const out: { x: number; y: number; id: string; height: number }[] = [];
  const defs = furnitureCatalog.filter((d) => d.rooms.includes(kit) && !d.store);
  if (!defs.length || kit === 'open') return out;
  const at = (x: number, y: number) => y * w + x;
  const is = (x: number, y: number, c: number) => x >= 0 && y >= 0 && x < w && y < h && cells[at(x, y)] === c;
  const seen = new Uint8Array(w * h);
  const N4 = [[1, 0], [-1, 0], [0, 1], [0, -1]] as const;
  for (let sy = 1; sy < h - 1; sy++) for (let sx = 1; sx < w - 1; sx++) {
    if (seen[at(sx, sy)] || cells[at(sx, sy)] !== CELL_FLOOR) continue;
    // flood one room
    const room: [number, number][] = []; const stack: [number, number][] = [[sx, sy]]; seen[at(sx, sy)] = 1;
    while (stack.length) { const [x, y] = stack.pop()!; room.push([x, y]); for (const [dx, dy] of N4) { const nx = x + dx, ny = y + dy; if (is(nx, ny, CELL_FLOOR) && !seen[at(nx, ny)]) { seen[at(nx, ny)] = 1; stack.push([nx, ny]); } } }
    if (room.length < 4) continue;
    const want = Math.min(5, Math.floor(room.length / 7));
    // classify cells
    const nearDoorOrStairs = (x: number, y: number) => N4.some(([dx, dy]) => is(x + dx, y + dy, CELL_DOOR) || is(x + dx, y + dy, CELL_STAIRS) || is(x + dx, y + dy, CELL_ELEV)) || doors.some(([dx, dy]) => Math.abs(dx - x) + Math.abs(dy - y) <= 2);
    const wallAdj = (x: number, y: number) => N4.some(([dx, dy]) => is(x + dx, y + dy, CELL_WALL) || is(x + dx, y + dy, CELL_WINDOW));
    // "back" = the room's cells farthest from the entrance side; "front" = nearest
    const depth = (x: number, y: number) => side === 0 ? y : side === 2 ? h - 1 - y : side === 1 ? w - 1 - x : x; // small = near the entrance side
    const depths = room.map(([x, y]) => depth(x, y)); const minD = Math.min(...depths), maxD = Math.max(...depths);
    const connected = (): boolean => {
      // all floor cells of this room still reachable from its first cell
      const start = room.find(([x, y]) => cells[at(x, y)] === CELL_FLOOR); if (!start) return true;
      const vis = new Set<number>([at(start[0], start[1])]); const st: [number, number][] = [start];
      while (st.length) { const [x, y] = st.pop()!; for (const [dx, dy] of N4) { const nx = x + dx, ny = y + dy; if (is(nx, ny, CELL_FLOOR) && !vis.has(at(nx, ny))) { vis.add(at(nx, ny)); st.push([nx, ny]); } } }
      return room.every(([x, y]) => cells[at(x, y)] !== CELL_FLOOR || vis.has(at(x, y)));
    };
    for (let k = 0; k < want; k++) {
      const def = rng.weighted(defs, (d) => d.density); if (!def) break;
      const cands = room.filter(([x, y]) => cells[at(x, y)] === CELL_FLOOR && !nearDoorOrStairs(x, y) && (def.indoor === 'anywhere' ? true : def.indoor === 'back' ? depth(x, y) === maxD && wallAdj(x, y) : def.indoor === 'front' ? depth(x, y) === minD && wallAdj(x, y) : wallAdj(x, y)));
      if (!cands.length) continue;
      const [x, y] = rng.pick(cands);
      cells[at(x, y)] = CELL_FURN;
      if (!connected()) { cells[at(x, y)] = CELL_FLOOR; continue; }
      out.push({ x, y, id: def.id, height: def.height });
    }
  }
  return out;
}

/** Cell at a world tile, or 0 when outside the plan. */
export function cellAt(plan: FloorPlan, x: number, y: number): number {
  const lx = Math.floor(x) - plan.x, ly = Math.floor(y) - plan.y;
  if (lx < 0 || ly < 0 || lx >= plan.w || ly >= plan.h) return 0;
  return plan.cells[ly * plan.w + lx];
}

/** Is this cell passable on foot? (doors, floor, stairs, elevator) */
/** Wall thickness: outer walls half a tile against the outer edge, windows a thin pane on that edge, inner walls a third of a
 * tile down the middle of their line (owner: walls were a whole tile, windows sat in the middle of them). */
export const OUTER_WALL = 0.5, WINDOW_PANE = 0.22, INNER_WALL = 0.34;
/** The solid slabs of a wall/window cell in local tile units ([x0, y0, x1, y1] inside the unit square); a door cell returns the slab it sits in (for drawing — it is walkable). */
export function wallSlabs(plan: FloorPlan, lx: number, ly: number): [number, number, number, number][] {
  const c = plan.cells[ly * plan.w + lx];
  if (c !== CELL_WALL && c !== CELL_WINDOW && c !== CELL_DOOR) return [];
  const out: [number, number, number, number][] = [];
  const edgeT = c === CELL_WINDOW ? WINDOW_PANE : OUTER_WALL;
  const onL = lx === 0, onR = lx === plan.w - 1, onT = ly === 0, onB = ly === plan.h - 1;
  const solid = (x: number, y: number): boolean => { if (x < 0 || y < 0 || x >= plan.w || y >= plan.h) return false; const q = plan.cells[y * plan.w + x]; return q === CELL_WALL || q === CELL_WINDOW || q === CELL_DOOR; };
  const a = 0.5 - INNER_WALL / 2, b = 0.5 + INNER_WALL / 2;
  if (onT) out.push([0, 0, 1, edgeT]); if (onB) out.push([0, 1 - edgeT, 1, 1]); if (onL) out.push([0, 0, edgeT, 1]); if (onR) out.push([1 - edgeT, 0, 1, 1]);
  if (out.length) {
    // an inner wall meeting the outer wall: a stub across the inner half so the two join
    if (c === CELL_WALL) { if (onT && !onB && solid(lx, ly + 1)) out.push([a, edgeT, b, 1]); if (onB && !onT && solid(lx, ly - 1)) out.push([a, 0, b, 1 - edgeT]); if (onL && !onR && solid(lx + 1, ly)) out.push([edgeT, a, 1, b]); if (onR && !onL && solid(lx - 1, ly)) out.push([0, a, 1 - edgeT, b]); }
    return out;
  }
  // an inner wall: a band along the run (walls left/right → horizontal; up/down → vertical; both at a junction)
  const h = solid(lx - 1, ly) || solid(lx + 1, ly), v = solid(lx, ly - 1) || solid(lx, ly + 1);
  if (h || !v) out.push([0, a, 1, b]);
  if (v) out.push([a, 0, b, 1]);
  return out;
}
export function cellWalkable(c: number): boolean { return c === CELL_FLOOR || c === CELL_DOOR || c === CELL_STAIRS || c === CELL_ELEV; }

/**
 * Shortest walk between two tiles of a plan (BFS over walkable cells, 4-neighbour), as a
 * list of tile centres excluding the start. Doors/stairs count as walkable; `outside`
 * tiles (beyond the footprint) are allowed only as the final target. Undefined = no route.
 */
export function planPath(plan: FloorPlan, sx: number, sy: number, tx: number, ty: number): [number, number][] | undefined {
  const ox = plan.x, oy = plan.y, w = plan.w, h = plan.h;
  const s = [Math.floor(sx) - ox, Math.floor(sy) - oy], t = [Math.floor(tx) - ox, Math.floor(ty) - oy];
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h;
  const walk = (x: number, y: number) => inside(x, y) ? cellWalkable(plan.cells[y * w + x]) : false;
  // the target may be just outside a door: route to the door cell next to it
  let goal: [number, number] = [t[0], t[1]];
  if (!inside(goal[0], goal[1]) || !walk(goal[0], goal[1])) {
    const near = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).map(([dx, dy]) => [goal[0] + dx, goal[1] + dy] as [number, number]).find(([x, y]) => walk(x, y));
    if (!near) return undefined;
    goal = near;
  }
  if (!walk(s[0], s[1])) {
    const near = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).map(([dx, dy]) => [s[0] + dx, s[1] + dy] as [number, number]).find(([x, y]) => walk(x, y));
    if (!near) return undefined;
    s[0] = near[0]; s[1] = near[1];
  }
  const prev = new Int32Array(w * h).fill(-1);
  const queue: number[] = [s[1] * w + s[0]];
  prev[s[1] * w + s[0]] = s[1] * w + s[0];
  const gi = goal[1] * w + goal[0];
  let found = false;
  for (let qi = 0; qi < queue.length && !found; qi++) {
    const cur = queue[qi]; const cx = cur % w, cy = Math.floor(cur / w);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = cx + dx, ny = cy + dy; if (!walk(nx, ny)) continue;
      const ni = ny * w + nx; if (prev[ni] >= 0) continue;
      prev[ni] = cur; if (ni === gi) { found = true; break; } queue.push(ni);
    }
  }
  if (!found && gi !== s[1] * w + s[0]) return undefined;
  const path: [number, number][] = [];
  for (let i = gi; i !== s[1] * w + s[0]; i = prev[i]) path.push([ox + (i % w) + 0.5, oy + Math.floor(i / w) + 0.5]);
  path.reverse();
  if (!inside(t[0], t[1]) || !walk(t[0], t[1])) path.push([tx, ty]);
  return path;
}
